import { sendRequest } from '@infrastructure/api/api';
import ApiResponse from '@core/types/apiResponse';
import { ApiMessageResponse } from '@core/types/apiMessageResponse';
import {
  ActivityEntryListResponse,
  ActivityEntryLogsQueryParams,
  ActivityEntryQueryParams,
  ActivityEntryStatusLog,
} from '../types/activityEntry';
import { buildActivityEntryLogsUrl } from '../constants/activityEntryLogs';
import {
  RequestActivityEntryReviewRequest,
  SaveActivityEntryDraftRequest,
  SubmitActivityEntryRequest,
} from '../types/localReportSubmissionRequests';
import { ReportTemplateAssignmentPayload, SyncReportTemplatesOptions } from '@features/report/template/types/reportTemplateAssignment';

export const fetchChurchActivityEntryForm = async (params: any): Promise<ApiResponse<any>> => {
  // Extract relevant parameters for the request
  const { churchId, ...fetchParams } = params;
  const query = new URLSearchParams();

  Object.entries(fetchParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });

  const qs = query.toString();
  return await sendRequest<any, any>('GET', `/church/${churchId}/activityentry/form${qs ? `?${qs}` : ''}`);
};

export const saveActivityDraftAsync = async (churchId: number, data: SaveActivityEntryDraftRequest): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<SaveActivityEntryDraftRequest, ApiMessageResponse>('POST', `/church/${churchId}/activityentry/save`, data);
};

export const submitActivityEntryAsync = async (churchId: number, data: SubmitActivityEntryRequest): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<SubmitActivityEntryRequest, ApiMessageResponse>('POST', `/church/${churchId}/activityentry/submit`, data);
}

export const fetchActivityEntryStatuses = async (): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>('GET', `/activityentry/status/query?all=1`);
};

export const fetchChurchActivityEntries = async (
  churchId: number,
  params: ActivityEntryQueryParams
): Promise<ApiResponse<ActivityEntryListResponse>> => {
  const query = new URLSearchParams();
  if (params.activityEntryStatus) query.set('activityEntryStatus', String(params.activityEntryStatus));
  if (params.reportTagId) query.set('reportTagId', String(params.reportTagId));
  if (params.activityEntryId) query.set('activityEntryId', String(params.activityEntryId));
  if (params.reportTemplateId) query.set('reportTemplateId', String(params.reportTemplateId));
  if (params.departmentId) query.set('departmentId', String(params.departmentId));
  if (params.page) query.set('page', String(params.page));
  if (params.perPage) query.set('perPage', String(params.perPage));
  if (params.freetext) query.set('freetext', params.freetext);

  const qs = query.toString();
  return await sendRequest<any, ActivityEntryListResponse>('GET', `/church/${churchId}/activityentry/query${qs ? `?${qs}` : ''}`);
};

export const fetchActivityEntryLogs = async (
  scope: string,
  entityId: number,
  params: ActivityEntryLogsQueryParams
): Promise<ApiResponse<ActivityEntryStatusLog[]>> => {
  return await sendRequest<any, ActivityEntryStatusLog[]>(
    'GET',
    buildActivityEntryLogsUrl(scope, entityId, params)
  );
};

export const requestActivityEntryReviewAsync = async (
  churchId: number,
  reportTemplateId: number,
  reportTagId: number,
  data: RequestActivityEntryReviewRequest
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<RequestActivityEntryReviewRequest, ApiMessageResponse>(
    'POST',
    `/church/${churchId}/activityentry/requestreview/template/${reportTemplateId}/tag/${reportTagId}`,
    data
  );
};

export const requestDistrictActivityEntryReviewAsync = async (
  districtId: number,
  reportTemplateId: number,
  reportTagId: number,
  data: RequestActivityEntryReviewRequest
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<RequestActivityEntryReviewRequest, ApiMessageResponse>(
    'POST',
    `/district/${districtId}/activityentry/requestreview/template/${reportTemplateId}/tag/${reportTagId}`,
    data
  );
};

export const requestNationActivityEntryReviewAsync = async (
  nationId: number,
  reportTemplateId: number,
  reportTagId: number,
  data: RequestActivityEntryReviewRequest
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<RequestActivityEntryReviewRequest, ApiMessageResponse>(
    'POST',
    `/nation/${nationId}/activityentry/requestreview/template/${reportTemplateId}/tag/${reportTagId}`,
    data
  );
};

export const fetchDistrictActivityEntries = async (
  districtId: number,
  params: ActivityEntryQueryParams
): Promise<ApiResponse<ActivityEntryListResponse>> => {
  const query = new URLSearchParams();
  if (params.activityEntryStatus) query.set('activityEntryStatus', String(params.activityEntryStatus));
  if (params.reportTagId) query.set('reportTagId', String(params.reportTagId));
  if (params.activityEntryId) query.set('activityEntryId', String(params.activityEntryId));
  if (params.reportTemplateId) query.set('reportTemplateId', String(params.reportTemplateId));
  if (params.departmentId) query.set('departmentId', String(params.departmentId));
  if (params.page) query.set('page', String(params.page));
  if (params.perPage) query.set('perPage', String(params.perPage));
  if (params.freetext) query.set('freetext', params.freetext);

  const qs = query.toString();
  return await sendRequest<any, ActivityEntryListResponse>('GET', `/district/${districtId}/activityentry/query${qs ? `?${qs}` : ''}`);
};

export const fetchNationActivityEntries = async (
  nationId: number,
  params: ActivityEntryQueryParams
): Promise<ApiResponse<ActivityEntryListResponse>> => {
  const query = new URLSearchParams();
  if (params.activityEntryStatus) query.set('activityEntryStatus', String(params.activityEntryStatus));
  if (params.reportTagId) query.set('reportTagId', String(params.reportTagId));
  if (params.activityEntryId) query.set('activityEntryId', String(params.activityEntryId));
  if (params.reportTemplateId) query.set('reportTemplateId', String(params.reportTemplateId));
  if (params.departmentId) query.set('departmentId', String(params.departmentId));
  if (params.page) query.set('page', String(params.page));
  if (params.perPage) query.set('perPage', String(params.perPage));
  if (params.freetext) query.set('freetext', params.freetext);

  const qs = query.toString();
  return await sendRequest<any, ActivityEntryListResponse>('GET', `/nation/${nationId}/activityentry/query${qs ? `?${qs}` : ''}`);
};

export const fetchDistrictActivityEntryForm = async (params: {
  districtId: number;
  reportTemplateId: number;
  reportTagId: number;
  page?: number;
  perPage?: number;
}): Promise<ApiResponse<any>> => {
  const query = new URLSearchParams({
    reportTemplateId: String(params.reportTemplateId),
    reportTagId: String(params.reportTagId),
  });
  if (params.page) query.set('page', String(params.page));
  if (params.perPage) query.set('perPage', String(params.perPage));

  return await sendRequest<any, any>(
    'GET',
    `/district/${params.districtId}/activityentry/form?${query.toString()}`
  );
};

export const fetchNationActivityEntryForm = async (params: {
  nationId: number;
  reportTemplateId: number;
  reportTagId: number;
  page?: number;
  perPage?: number;
}): Promise<ApiResponse<any>> => {
  const query = new URLSearchParams({
    reportTemplateId: String(params.reportTemplateId),
    reportTagId: String(params.reportTagId),
  });
  if (params.page) query.set('page', String(params.page));
  if (params.perPage) query.set('perPage', String(params.perPage));

  return await sendRequest<any, any>(
    'GET',
    `/nation/${params.nationId}/activityentry/form?${query.toString()}`
  );
};

export const saveDistrictActivityDraftAsync = async (
  districtId: number,
  data: SaveActivityEntryDraftRequest
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<SaveActivityEntryDraftRequest, ApiMessageResponse>(
    'POST',
    `/district/${districtId}/activityentry/save`,
    data
  );
};

export const submitDistrictActivityEntryAsync = async (
  districtId: number,
  data: SubmitActivityEntryRequest
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<SubmitActivityEntryRequest, ApiMessageResponse>(
    'POST',
    `/district/${districtId}/activityentry/submit`,
    data
  );
};

export const saveNationActivityDraftAsync = async (
  nationId: number,
  data: SaveActivityEntryDraftRequest
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<SaveActivityEntryDraftRequest, ApiMessageResponse>(
    'POST',
    `/nation/${nationId}/activityentry/save`,
    data
  );
};

export const submitNationActivityEntryAsync = async (
  nationId: number,
  data: SubmitActivityEntryRequest
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<SubmitActivityEntryRequest, ApiMessageResponse>(
    'POST',
    `/nation/${nationId}/activityentry/submit`,
    data
  );
};

export const syncNationReportTemplates = async (
  nationId: number,
  departmentId: number,
  templates: ReportTemplateAssignmentPayload[],
  options?: SyncReportTemplatesOptions
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<
    {
      department_id: number;
      report_templates: ReportTemplateAssignmentPayload[];
      confirm_clear_all?: boolean;
    },
    ApiMessageResponse
  >('POST', `nation/${nationId}/reporttemplate/sync`, {
    department_id: departmentId,
    report_templates: templates,
    ...(options?.confirmClearAll ? { confirm_clear_all: true } : {}),
  });
};

export const assignNationReportTemplates = async (
  nationId: number,
  departmentId: number,
  templates: ReportTemplateAssignmentPayload[]
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<
    { report_templates: ReportTemplateAssignmentPayload[]; department_id: number },
    ApiMessageResponse
  >('POST', `nation/${nationId}/reporttemplate/assign`, {
    report_templates: templates,
    department_id: departmentId,
  });
};

export const removeNationReportTemplates = async (
  nationId: number,
  reportTemplateIds: number[]
): Promise<ApiResponse<ApiMessageResponse>> => {
  return await sendRequest<{ report_template_ids: number[] }, ApiMessageResponse>(
    'POST',
    `nation/${nationId}/reporttemplate/remove`,
    { report_template_ids: reportTemplateIds }
  );
};

type ActivityEntryPrefillParams = {
  reportTemplateId: number;
  reportTagId: number;
  sourceReportTagId?: number;
};

function buildPrefillQuery(params: ActivityEntryPrefillParams): string {
  const query = new URLSearchParams({
    reportTemplateId: String(params.reportTemplateId),
    reportTagId: String(params.reportTagId),
  });
  if (params.sourceReportTagId) {
    query.set('sourceReportTagId', String(params.sourceReportTagId));
  }
  return query.toString();
}

export const fetchChurchActivityEntryPrefill = async (
  churchId: number,
  params: ActivityEntryPrefillParams
): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>(
    'GET',
    `/church/${churchId}/activityentry/prefill?${buildPrefillQuery(params)}`
  );
};

export const fetchDistrictActivityEntryPrefill = async (
  districtId: number,
  params: ActivityEntryPrefillParams
): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>(
    'GET',
    `/district/${districtId}/activityentry/prefill?${buildPrefillQuery(params)}`
  );
};

export const fetchNationActivityEntryPrefill = async (
  nationId: number,
  params: ActivityEntryPrefillParams
): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>(
    'GET',
    `/nation/${nationId}/activityentry/prefill?${buildPrefillQuery(params)}`
  );
};